import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import clsx from "clsx";

import PaginationFooter from "common/components/PaginationFooter";
import {
  Table,
  TableCell,
  TableHead,
  TableRow,
} from "common/components/ui/TableV2";
import usePagination from "common/hooks/usePagination";

interface SimpleDataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  localStorageId: string;
}

export default function SimpleDataTable<TData, TValue>({
  columns,
  data,
  localStorageId,
}: SimpleDataTableProps<TData, TValue>) {
  const { page, setPage, pageSize, setPageSize } =
    usePagination(localStorageId);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: { pagination: { pageIndex: page, pageSize } },
    autoResetPageIndex: false,
    onPaginationChange: (updaterOrValue) => {
      const next =
        typeof updaterOrValue === "function"
          ? updaterOrValue({ pageIndex: page, pageSize })
          : updaterOrValue;

      setPage(next.pageIndex);
      setPageSize(next.pageSize);
    },
  });

  return (
    <>
      <Table>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  className={clsx(
                    header.column.columnDef.meta?.className,
                    header.column.columnDef.meta?.headClassName,
                  )}
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
                </TableHead>
              ))}
            </tr>
          ))}
        </thead>

        <tbody>
          {table.getRowModel().rows.map((row) => {
            const visibleCells = row.getVisibleCells();

            return (
              <TableRow key={row.id}>
                {visibleCells.map((cell, index) => {
                  const isLastCell = index === visibleCells.length - 1;

                  return (
                    <TableCell
                      key={cell.id}
                      className={clsx(
                        cell.column.columnDef.meta?.className,
                        cell.column.columnDef.meta?.bodyClassName,
                        isLastCell && "w-0",
                      )}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </tbody>
      </Table>

      <PaginationFooter
        selectedPage={page}
        setSelectedPage={(page) => table.setPageIndex(page)}
        pageSize={pageSize}
        setPageSize={(newPageSize) => table.setPageSize(newPageSize)}
        totalItemCount={data.length}
        isLastPageHidden={false}
      />
    </>
  );
}
